import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  db, claimMutation, getPendingMutationsCount, getAttentionCount, MutationEntry,
  pruneCompletedMutations, recoverStaleSyncing, SYNC_BATCH_SIZE,
} from './db';
import { useConnectivity } from './useConnectivity';
import { replayRegistry, TerminalError } from './replayRegistry';
import { applyReplayResult, type ReplayResult } from './replayCore';
import { deleteAttachmentsForOutbox } from './attachments';
import { isDue, nextAttemptAt } from './retryBackoff';
import {
  detectBackgroundSync, isBackgroundSyncSupported, requestOutboxSync, subscribeToDrainMessages,
} from '../pwa/backgroundSync';

export interface SyncContextValue {
  isSyncing: boolean;
  pendingCount: number;
  attentionCount: number;                 // failed + conflict (needs a human)
  lastSyncAt: Date | null;
  syncError: string | null;
  /** Can this browser send the outbox with no tab open (Background Sync)?
   *  False on iOS Safari / Firefox — the unsent UI says to keep the app open. */
  backgroundSyncSupported: boolean;
  triggerSync: () => Promise<void>;
  refresh: () => Promise<void>;
  retry: (id: number) => Promise<void>;
  discard: (id: number) => Promise<void>;
  resolveConflict: (id: number) => Promise<void>;   // re-send with force: true
}

export const SyncContext = createContext<SyncContextValue | null>(null);

export function SyncProvider({ children }: { children: ReactNode }) {
  const { isOnline } = useConnectivity();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [attentionCount, setAttentionCount] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [backgroundSyncSupported, setBackgroundSyncSupported] = useState(isBackgroundSyncSupported);

  // Live only while this provider is mounted. Every async path here talks to
  // IndexedDB, which can resolve long after a route change — or, in tests,
  // after the environment itself is torn down (that produced an unhandled
  // "window is not defined" rejection that failed CI while every test passed).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const [pending, attention] = await Promise.all([
      getPendingMutationsCount(),
      getAttentionCount(),
    ]);
    if (!mounted.current) return; // unmounted mid-read — drop the result
    setPendingCount(pending);
    setAttentionCount(attention);
  }, []);

  /* Claim → replay → settle. The claim is atomic and stamps a 60 s lease
     (`leaseUntil`) so the service worker, or another tab, leaves this row
     alone while we hold it; replayCore renews the lease at every progress
     save. `applyReplayResult` is the same settle the worker uses, so a row
     ends up in the same state whoever replayed it. */
  const processMutation = async (m: MutationEntry): Promise<void> => {
    const claimed = await claimMutation(m.id!, new Date());
    if (!claimed) return; // taken by another context since we listed it

    const replay = replayRegistry[claimed.entityType];
    if (!replay) {
      await db.mutations.update(claimed.id!, {
        status: 'failed', error: `No replay handler for ${claimed.entityType}`, leaseUntil: undefined,
      });
      return;
    }

    let result: ReplayResult;
    try {
      const outcome = await replay(claimed);
      if (outcome.status === 'conflict') {
        await db.mutations.update(claimed.id!, {
          status: 'conflict', conflicts: outcome.conflicts, error: 'Schedule conflict', leaseUntil: undefined,
        });
        return;
      }
      result = { outcome: 'done' };
    } catch (e) {
      result = e instanceof TerminalError
        ? { outcome: 'terminal', message: e.message }
        : { outcome: 'retry', message: e instanceof Error ? e.message : 'Sync error', error: e };
    }
    await applyReplayResult(db, claimed, result);

    // Anything still owed: ask the browser to finish it even if this tab
    // closes (no-op where Background Sync is absent).
    if (result.outcome !== 'done') void requestOutboxSync();
  };

  /* Nothing else re-runs a backed-off item: `pendingCount` does not change
     while it waits, so the auto-sync effect below stays quiet and the item
     would sit there until the next connectivity flip. One timer, armed for the
     earliest due item, is what actually makes the retry happen. */
  const wakeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const triggerSyncRef = useRef<() => void>(() => {});

  const scheduleWake = useCallback(async () => {
    clearTimeout(wakeTimer.current);
    const pending = await db.mutations.where('status').equals('pending').toArray();
    if (!mounted.current) return;

    const now = Date.now();
    const waits = pending
      .map((m) => nextAttemptAt(m)?.getTime())
      .filter((t): t is number => typeof t === 'number' && t > now);
    if (waits.length === 0) return;

    // A floor keeps a clock skew or an almost-due item from busy-looping.
    const delay = Math.max(1_000, Math.min(...waits) - now);
    wakeTimer.current = setTimeout(() => triggerSyncRef.current(), delay);
  }, []);

  const triggerSync = useCallback(async () => {
    if (isSyncing || !isOnline) return;
    setIsSyncing(true);
    setSyncError(null);
    try {
      // A lease that lapsed since boot (a tab killed mid-replay, a worker
      // pass that died) goes back to pending before we list the batch.
      await recoverStaleSyncing(new Date());
      /* Filter by due BEFORE taking the batch, not after: slicing first would
         let a handful of backed-off items fill the batch and starve work that
         is ready to go. */
      const pending = await db.mutations.where('status').equals('pending').toArray();
      const now = new Date();
      const due = pending.filter((m) => isDue(m, now)).slice(0, SYNC_BATCH_SIZE);
      for (const m of due) await processMutation(m);
      setLastSyncAt(new Date());
      await refresh();
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
      await scheduleWake();
    }
  }, [isSyncing, isOnline, refresh, scheduleWake]);

  useEffect(() => {
    triggerSyncRef.current = triggerSync;
  }, [triggerSync]);

  useEffect(() => () => clearTimeout(wakeTimer.current), []);

  const retry = useCallback(async (id: number) => {
    await db.mutations.update(id, { status: 'pending', attempts: 0, error: undefined });
    await refresh();
    triggerSync();
  }, [refresh, triggerSync]);

  const discard = useCallback(async (id: number) => {
    const m = await db.mutations.get(id);
    if (m) await deleteAttachmentsForOutbox(m.idempotencyKey);
    await db.mutations.delete(id);
    await refresh();
  }, [refresh]);

  const resolveConflict = useCallback(async (id: number) => {
    const m = await db.mutations.get(id);
    if (!m) return;
    const payload = { ...(m.payload as Record<string, unknown>), force: true };
    await db.mutations.update(id, { payload, status: 'pending', conflicts: undefined, error: undefined, attempts: 0 });
    await refresh();
    triggerSync();
  }, [refresh, triggerSync]);

  // Boot-time outbox hygiene, then the first count refresh (so recovered
  // rows count as pending and the auto-sync effect below picks them up).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const recovered = await recoverStaleSyncing();
        if (cancelled) return;
        if (recovered > 0) console.debug(`[sync] recovered ${recovered} item(s) stuck 'syncing' from a previous session`);
        const pruned = await pruneCompletedMutations();
        if (cancelled) return;
        if (pruned > 0) console.debug(`[sync] pruned ${pruned} completed outbox row(s) older than 30 days`);
        await refresh();
      } catch (e) {
        // Boot hygiene is best-effort: a browser that closed the database
        // mid-flight (or a test tearing down) must not surface an unhandled
        // rejection. Real failures still reach the console for diagnosis.
        if (!cancelled) console.debug('[sync] boot hygiene skipped', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // Auto-sync when connectivity returns and there is work to do.
  useEffect(() => {
    if (isOnline && pendingCount > 0) triggerSync();
  }, [isOnline, pendingCount, triggerSync]);

  // Background Sync (offline reliability §1). Whenever work is waiting —
  // queued while offline, or left over after a failed pass — register the
  // outbox sync so the service worker finishes it once a network is back,
  // tab or no tab. Idempotent, and a no-op without the API.
  useEffect(() => {
    if (pendingCount > 0) void requestOutboxSync();
  }, [pendingCount]);

  // The worker never replays while a window is open: on its sync event it
  // posts a drain message and this provider does the work (one implementation).
  useEffect(() => subscribeToDrainMessages(() => triggerSyncRef.current()), []);

  // The synchronous check says the API exists; the registration says whether
  // this worker actually exposes it. Refine once the worker is ready.
  useEffect(() => {
    if (!isBackgroundSyncSupported()) return;
    detectBackgroundSync().then((supported) => {
      if (mounted.current) setBackgroundSyncSupported(supported);
    });
  }, []);

  const value: SyncContextValue = {
    isSyncing, pendingCount, attentionCount, lastSyncAt, syncError, backgroundSyncSupported,
    triggerSync, refresh, retry, discard, resolveConflict,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
